import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, MenuItem } from "@mui/material";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import { useThemeMode } from "../../contexts/ThemeModeContext";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useLogoutMutation } from "../../api/authApi";
import { useListLibraryQuery } from "../../api/adminApi";
import amblem from "../../assets/img/terakki-amblem.png";
import amblemBeyaz from "../../assets/img/terakki-amblem-beyaz.png";

export const ADMIN_NAV_ITEMS = [
  { to: "/sistem/pano", key: "nav_admin_dashboard", Icon: InsightsOutlinedIcon },
  { to: "/sistem/kutuphane", key: "nav_admin_library", Icon: LibraryBooksOutlinedIcon, badge: "pending" },
  { to: "/sistem/kullanicilar", key: "nav_admin_users", Icon: PeopleOutlineIcon },
  { to: "/sistem/duyurular", key: "nav_admin_announcements", Icon: CampaignOutlinedIcon },
  { to: "/sistem/kayitlar", key: "nav_admin_audit", Icon: ReceiptLongOutlinedIcon },
];

/** Onay bekleyen paket sayısı (kütüphane listesinin sayaçlarından). */
function usePendingCount() {
  const { data } = useListLibraryQuery(
    { tab: "pending", page: 1 },
    { pollingInterval: 5 * 60 * 1000 }
  );
  return Number(data?.counts?.pending) || 0;
}

function useLogoutAction() {
  const { clearSession } = useAuth();
  const [logout] = useLogoutMutation();
  const navigate = useNavigate();
  return async () => {
    try {
      await logout().unwrap();
    } catch {
      // backend çıkışı başarısız olsa da yerel oturumu düşürüyoruz
    }
    clearSession();
    navigate("/giris", { replace: true });
  };
}

function PendingBadge({ count }) {
  if (!count) return null;
  return (
    <span className="ml-auto min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] leading-[22px] text-center bg-terakkiWarning/15 text-[#9A6412] dark:bg-[#F2C46D]/20 dark:text-[#F2C46D]">
      {count > 99 ? "99+" : count}
    </span>
  );
}

function ThemeToggle() {
  const { mode, toggle } = useThemeMode();
  const dark = mode === "dark";
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={dark ? "Açık tema" : "Koyu tema"}
      title={dark ? "Açık tema" : "Koyu tema"}
      className="w-10 h-10 p-0 leading-none shrink-0 rounded-full grid place-items-center bg-terakkiBrandCloud text-terakkiPrimaryDark hover:bg-terakkiBrandMist dark:bg-white/[0.1] dark:text-white dark:hover:bg-white/[0.16]"
    >
      {dark ? <LightModeOutlinedIcon sx={{ fontSize: 20, display: "block", m: 0 }} aria-hidden /> : <DarkModeOutlinedIcon sx={{ fontSize: 20, display: "block", m: 0 }} aria-hidden />}
    </button>
  );
}

/** Masaüstü sol ray: yalnız yönetim bölümleri; altta hesap (çıkış) ve tema. */
export default function AdminSideNav() {
  const { t } = useScriptMode();
  const { user } = useAuth();
  const { mode } = useThemeMode();
  const dark = mode === "dark";
  const [anchor, setAnchor] = useState(null);
  const pending = usePendingCount();
  const logout = useLogoutAction();

  const fullName = user ? `${user.name || ""} ${user.surname || ""}`.trim() : "";
  const displayName = fullName || user?.email || "";
  const initial = (user?.name || user?.email || "?").slice(0, 1).toUpperCase();

  return (
    <nav className="h-full min-h-screen bg-terakkiSurface border-r border-terakkiBorder w-60 flex flex-col dark:bg-white/[0.04] dark:border-white/[0.14]">
      {/* Marka: tanıtım sayfasına döner */}
      <Link to="/" title="Ana sayfa" aria-label="Terakki ana sayfa" className="flex items-center gap-2.5 px-4 h-16 border-b border-terakkiBorder hover:bg-terakkiSurfaceAlt transition-colors dark:border-white/[0.14] dark:hover:bg-white/[0.06]">
        <img src={dark ? amblemBeyaz : amblem} alt="" aria-hidden className="h-8 w-auto" />
        <span className="min-w-0">
          <span className="block text-lg font-SemiBold text-terakkiPrimaryDark tracking-tight leading-tight dark:text-white">
            {t("app_name")}
          </span>
          <span className="block text-xs text-terakkiNeutral leading-tight dark:text-white/60">Yönetim</span>
        </span>
      </Link>

      <div className="flex-1 p-3 flex flex-col gap-1">
        {ADMIN_NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              [
                "flex items-center gap-3 px-3 py-2 rounded-lg text-base transition-colors",
                isActive
                  ? "bg-terakkiBrandCloud text-terakkiPrimaryDark font-SemiBold dark:bg-white/[0.12] dark:text-white"
                  : "text-terakkiPrimaryDark hover:bg-terakkiSurfaceAlt dark:text-white/80 dark:hover:bg-white/[0.08]",
              ].join(" ")
            }
          >
            <item.Icon fontSize="small" aria-hidden />
            <span>{t(item.key)}</span>
            {item.badge === "pending" && <PendingBadge count={pending} />}
          </NavLink>
        ))}
      </div>

      <div className="p-3 border-t border-terakkiBorder dark:border-white/[0.14] flex items-center gap-2">
        <button
          type="button"
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={t("topbar_user_menu")}
          className="min-w-0 flex-1 flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-terakkiSurfaceAlt text-left dark:hover:bg-white/[0.08]"
        >
          <span className="w-9 h-9 shrink-0 rounded-full bg-terakkiPrimaryDark text-white flex items-center justify-center text-sm font-SemiBold dark:bg-white/[0.12]">
            {initial}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm text-terakkiInk truncate dark:text-white">{displayName}</span>
            <span className="block text-xs text-terakkiNeutral truncate dark:text-white/70">Sistem hesabı</span>
          </span>
        </button>
        <ThemeToggle />
        <Menu
          open={Boolean(anchor)}
          anchorEl={anchor}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <MenuItem
            onClick={() => {
              setAnchor(null);
              logout();
            }}
          >
            <LogoutIcon fontSize="small" className="mr-2" aria-hidden />
            {t("nav_logout")}
          </MenuItem>
        </Menu>
      </div>
    </nav>
  );
}

/** Dar ekran: sol ray gizli; üstte yatay kayan bölüm şeridi, tema ve çıkış. */
export function AdminMobileBar() {
  const { t } = useScriptMode();
  const { mode } = useThemeMode();
  const dark = mode === "dark";
  const pending = usePendingCount();
  const logout = useLogoutAction();

  return (
    <div className="md:hidden -mx-4 -mt-4 sm:-mx-6 sm:-mt-6 mb-4 border-b border-terakkiBorder bg-terakkiSurface dark:bg-white/[0.04] dark:border-white/[0.14]">
      <div className="flex items-center gap-2.5 px-4 h-14">
        <Link to="/" title="Ana sayfa" aria-label="Terakki ana sayfa" className="shrink-0">
          <img src={dark ? amblemBeyaz : amblem} alt="" aria-hidden className="h-7 w-auto" />
        </Link>
        <span className="flex-1 min-w-0 text-md text-terakkiPrimaryDark dark:text-white truncate">
          {t("nav_admin_section")}
        </span>
        <ThemeToggle />
        <button
          type="button"
          onClick={logout}
          aria-label={t("nav_logout")}
          title={t("nav_logout")}
          className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center text-terakkiPrimaryDark hover:bg-terakkiBrandCloud dark:text-white dark:hover:bg-white/[0.08]"
        >
          <LogoutIcon fontSize="small" aria-hidden />
        </button>
      </div>
      <div className="flex gap-1 px-3 pb-2 overflow-x-auto">
        {ADMIN_NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              [
                "shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm whitespace-nowrap",
                isActive
                  ? "bg-terakkiBrandCloud text-terakkiPrimaryDark dark:bg-white/[0.12] dark:text-white"
                  : "text-terakkiNeutral dark:text-white/70",
              ].join(" ")
            }
          >
            <item.Icon sx={{ fontSize: 16 }} aria-hidden />
            {t(item.key)}
            {item.badge === "pending" && pending > 0 && (
              <span className="text-[11px] text-[#9A6412] dark:text-[#F2C46D]">· {pending}</span>
            )}
          </NavLink>
        ))}
      </div>
    </div>
  );
}
